import { chain, nth, path, propOr, defaultTo, prop } from 'ramda';
import { WizardState } from './types';
import { get } from 'http';
import { StepState } from '../wizard.types';
import { application } from '../../../../_runtime/bootstrap';

/**
 * Function: getCurrentIndex
 * 
 * This function retrieves the current index from the path 'step'.
 * 
 * @returns {number} The current index value.
 */
export const getCurrentIndex = path<number>(['step'])!;

/**
 * Function: getCurrentStep
 * 
 * This function retrieves the current step from the wizard state.
 * 
 * @param {WizardState} state - The state of the wizard.
 * @returns {Function} A function that returns the current step.
 */
export const getCurrentStep = (state: WizardState) => () =>
  nth(getCurrentIndex(state.step)!, defaultTo([], state.steps));

/**
 * Function: getSteps
 * 
 * This function retrieves the steps from the wizard state.
 * 
 * @param {WizardState} obj - The state of the wizard.
 * @returns {Array} An array of steps.
 */
export const getSteps = (obj: WizardState) => obj?.steps ?? [];

/**
 * Function: initialRequests
 * 
 * This asynchronous function performs initial data requests needed for the wizard's initial state.
 * 
 * @param {WizardState} initialState - The initial state of the wizard.
 * @param {Function} setIsLoading - Function to set the loading state.
 * @param {Function} setInitialState - Function to set the initial state.
 * @param {boolean} [isInCourseAssignment=false] - Flag indicating if it is in course assignment.
 * @returns {Promise<void>} A promise that resolves once the data has been fetched and the state has been updated.
 */
export const initialRequests = async (
  initialState: WizardState,
  setIsLoading: any,
  setInitialState: any,
  isInCourseAssignment: boolean = false
) => {
  try {
    const elementsPromise = application
      .getHttpClient()
      .get('/buildingsms/types/elements');
    const spaceFunctionsPromise: any = application
      .getHttpClient()
      .get('/buildingms/presets?type=SPACES');
    const buildingParametersPromise: any = application
      .getHttpClient()
      .get('/buildingms/presets?type=BUILDING_PARAMETERS');

    const [
      elementsResponse,
      spaceFunctionsResponse,
      buildingParametersResponse,
    ]: any = await Promise.all([
      elementsPromise,
      spaceFunctionsPromise,
      buildingParametersPromise,
    ]);

    const elements = elementsResponse.body.items;
    const spaceFunctions = spaceFunctionsResponse.body.items;
    const buildingParameters = buildingParametersResponse.body.items;

    setInitialState({
      ...initialState,
      spaceFunctions,
      elementTypesOptions: elements,
      buildingParameters,
      isInCourseAssignment,
    });

    setTimeout(() => setIsLoading(false), 1000);
  } catch (error) {
    console.error('Error fetching data:', error);
  }
};

/**
 * Function: mapDataToDropdownOptions
 * 
 * This function maps data to dropdown options by extracting the value and text fields from each item.
 * 
 * @param {any} data - The data to be mapped.
 * @param {string} valueField - The field to be used for the option value.
 * @param {string} textField - The field to be used for the option text.
 * @returns {Array} An array of objects representing dropdown options.
 */
export const mapDataToDropdownOptions = (
  data: any,
  valueField: string,
  textField: string
) => {
  return data.map((x: any) => ({
    value: x[valueField],
    text: x[textField],
  }));
};

/**
 * Function: calculateLowestRoomLength
 * 
 * Calculates the lowest room length from two sets of rooms, taking into account whether the rooms are modular.
 * 
 * @param {Array} rowFirstRooms - An array of rooms in the first row.
 * @param {Array} rowSecondRooms - An array of rooms in the second row.
 * @param {boolean} isModular - A boolean indicating if the rooms are modular.
 * @param {number} moduleSize - The size of the module, if rooms are modular.
 * 
 * @returns {number} - The lowest room length, adjusted for modular size if applicable.
 */
export const calculateLowestRoomLength = (
  rowFirstRooms: any,
  rowSecondRooms: any,
  isModular: boolean,
  moduleSize: number
) => {
  let lowestLength = Infinity;

  rowFirstRooms.map((room: any) => {
    if (room.length.creationType === "RANDOM") {
      if (room.length.options.min < lowestLength) {
        lowestLength = room.length.options.min;
      }
    } else {
      if (room.length.value < lowestLength) {
        lowestLength = room.length.value;
      }
    }
    return room;
  });

  rowSecondRooms.map((room: any) => {
    if (room.length.creationType === "RANDOM") {
      if (room.length.options.min < lowestLength) {
        lowestLength = room.length.options.min;
      }
    } else {
      if (room.length.value < lowestLength) {
        lowestLength = room.length.value;
      }
    }
    return room;
  });

  if (isModular) {
    return lowestLength * moduleSize;
  } else return lowestLength;
};
